import { AxiosError } from 'axios';
import { createContext, PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { backendSubscriptionService } from '../../../request/services/backend-subscription.service';
import { Integration } from '../../../shared/interfaces/integration';
import { EntityUIState } from '../../../request/interfaces/entity-ui-state';
import { useQuery } from '../../../request/hooks/useQuery';
import { PipelineAPI } from '../../api/PipelineAPI';
import { IntegrationFactory, PipelineStatusFactory } from '../../factories/integrationFactory';
import { shouldResumePipelineCreate } from '../../utils/pipelineUtil';

interface PipelineOverviewContextReturnType {
  pipeline?: Integration;
  uiState: EntityUIState;
  error: AxiosError | null;
  fetchPipeline: VoidFunction;
  updatePipeline: (pipeline: Integration) => void;
}

const initialState: PipelineOverviewContextReturnType = {
  pipeline: undefined,
  uiState: EntityUIState.NEW,
  error: null,
  fetchPipeline: () => {},
  updatePipeline: () => {}
};

export const PipelineOverviewContext = createContext<PipelineOverviewContextReturnType>({
  ...initialState
});

export function PipelineOverviewProvider({
  children,
  pipelineId
}: PropsWithChildren<{ pipelineId: number }>) {
  const navigate = useNavigate();

  const {
    data: pipeline,
    uiState,
    error,
    fetch: fetchPipeline,
    writeData: setPipeline
  } = useQuery({
    dataSource$: () =>
      PipelineAPI.getIntegration(pipelineId).then(res => {
        const _integration = IntegrationFactory(res.data);

        if (shouldResumePipelineCreate(_integration.status.value)) {
          navigate(`/pipeline/${_integration.id}/create`);
          return undefined;
        }

        return _integration;
      }),
    pollInterval: 5000,
    pollDataSource$: data =>
      PipelineAPI.getIntegrationStatus(pipelineId).then(res => {
        const integration = data as Integration;
        const status = PipelineStatusFactory(res.data);

        if (status.value !== integration.status.value) {
          backendSubscriptionService.events$.next({
            ev: 'PIPELINE_STATUS_UPDATED',
            ids: [pipelineId],
            timestamp: new Date().getTime()
          });

          return PipelineAPI.getIntegration(pipelineId).then(r => IntegrationFactory(r.data));
        }

        return {
          ...integration,
          status
        };
      })
  });

  useEffect(() => {
    fetchPipeline();
  }, [pipelineId]);

  const updatePipeline = useCallback((_pipeline: Integration) => {
    setPipeline(() => _pipeline);
  }, []);

  const contextValues = useMemo<PipelineOverviewContextReturnType>(() => {
    return {
      updatePipeline,
      fetchPipeline,
      error: error as AxiosError,
      uiState,
      pipeline
    };
  }, [uiState, error, updatePipeline, pipeline]);

  return (
    <PipelineOverviewContext.Provider value={contextValues}>
      {children}
    </PipelineOverviewContext.Provider>
  );
}
